import { useState, useCallback } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { Button } from "./ui/button";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { name: "Modules", href: "#modules" },
    { name: "How It Works", href: "#how-it-works" },
    { name: "Platform", href: "#platform" },
    { name: "VR Lab", href: "/vr-lab", isRoute: true },
    { name: "Tech", href: "/tech", isRoute: true },
    { name: "Team", href: "/team", isRoute: true },
    { name: "Contact", href: "#contact" },
  ];

  /** Scroll to the top of the page smoothly (used by logo click). */
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsOpen(false);
  }, []);

  /**
   * Handle anchor (#section) nav clicks.
   * With rootMargin="1200px" on InViewLoader, sections pre-render well before
   * the user scrolls to them, so the element is almost always in the DOM by
   * the time a nav link is clicked. We try immediately, and if somehow not
   * yet mounted we retry once per animation frame — no visible step-scrolling.
   */
  const handleAnchorClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      e.preventDefault();
      setIsOpen(false);

      const id = href.replace("#", "");

      const scrollTo = (attemptsLeft: number) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        if (attemptsLeft > 0) {
          requestAnimationFrame(() => scrollTo(attemptsLeft - 1));
        }
      };

      scrollTo(60); // ~1 second of rAF retries at 60fps, invisible to user
    },
    []
  );

  return (
    <nav className="fixed top-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-5xl px-4">
      <div className="glass-panel rounded-full shadow-elevated">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo — click scrolls to top */}
            <button
              onClick={scrollToTop}
              aria-label="Go to top"
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full"
            >
              <Logo />
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) =>
                link.isRoute ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => window.scrollTo({ top: 0 })}
                    className="text-muted-foreground hover:text-foreground transition-colors font-medium"
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => handleAnchorClick(e, link.href)}
                    className="text-muted-foreground hover:text-foreground transition-colors font-medium"
                  >
                    {link.name}
                  </a>
                )
              )}
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Button variant="ghost" className="text-muted-foreground hover:text-foreground" asChild>
                <a href="https://customer.vrxed.com/login" target="_blank" rel="noopener noreferrer">
                  Login
                </a>
              </Button>
              <Button variant="glow" asChild>
                <a href="https://customer.vrxed.com/register" target="_blank" rel="noopener noreferrer">
                  Get Started
                </a>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-foreground"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isOpen}
            >
              {isOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="md:hidden py-6 border-t border-border">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) =>
                  link.isRoute ? (
                    <Link
                      key={link.name}
                      to={link.href}
                      className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2"
                      onClick={() => { setIsOpen(false); window.scrollTo({ top: 0 }); }}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      key={link.name}
                      href={link.href}
                      className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2"
                      onClick={(e) => handleAnchorClick(e, link.href)}
                    >
                      {link.name}
                    </a>
                  )
                )}
                <div className="flex flex-col gap-3 pt-4 border-t border-border">
                  <Button variant="ghost" className="justify-start" asChild>
                    <a href="https://customer.vrxed.com/login" target="_blank" rel="noopener noreferrer">
                      Login
                    </a>
                  </Button>
                  <Button variant="glow" asChild>
                    <a href="https://customer.vrxed.com/register" target="_blank" rel="noopener noreferrer">
                      Get Started
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
